import { receiptLogoHtml } from './print-receipt.util';

/** Marca do documento: vem do BrandService do componente que dispara a impressão. */
export interface TclePrintBrand {
  logo?: string | null;
  cdEmpresa?: string | null;
}

function formatarCpf(cpf: string): string {
  const d = (cpf ?? '').replace(/\D/g, '');
  if (d.length !== 11) return cpf || '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

const CSS_TCLE = `
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; font-size: 10px; color: #000; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  @media print { body { margin: 0; padding: 15px; } @page { size: A4 portrait; margin: 15mm 15mm; } .no-print { display: none; } }
  .header-container { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 12px; }
  .header-logo { flex-shrink: 0; }
  /* Limita pela ALTURA: max-width só serve a logo quase quadrado. */
  .header-logo svg { height: 48px; width: auto; }
  .header-logo img { max-height: 48px; max-width: 100%; width: auto; height: auto; }
  .header-titles { text-align: center; flex-grow: 1; padding: 0 20px; }
  .header-titles .tcle-label { font-size: 9px; font-weight: bold; letter-spacing: 1px; color: #555; }
  .header-titles .tcle-nome { font-size: 14px; font-weight: bold; margin: 3px 0; }
  .header-titles .tcle-ref { font-size: 8px; color: #555; font-style: italic; }
  .section-header { font-weight: bold; font-size: 10px; background-color: #e8e8e8; padding: 4px 6px; border: 1px solid #000; margin: 10px 0 0 0; text-transform: uppercase; }
  .info-table { width: 100%; border-collapse: collapse; border: 1px solid #000; border-top: none; margin-bottom: 8px; }
  .info-table td { padding: 3px 6px; font-size: 10px; border: 1px solid #ccc; vertical-align: middle; }
  .info-table td.label { font-weight: bold; background-color: #f5f5f5; white-space: nowrap; width: 1px; }
  .declaro { margin: 8px 0; font-size: 10px; }
  .tipo-row { margin: 4px 0 10px 0; font-size: 10px; }
  .num-section { margin: 8px 0 4px 0; font-size: 10px; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
  .texto { font-size: 10px; text-align: justify; margin: 4px 0; line-height: 1.5; }
  .bullet-list { margin: 4px 0 4px 12px; padding: 0; list-style: none; }
  .bullet-list li { font-size: 10px; text-align: justify; margin: 2px 0; line-height: 1.5; padding-left: 8px; }
  .bullet-list li::before { content: "•"; margin-right: 4px; }
  .salvaguardas { background-color: #fafafa; border: 1px solid #ccc; padding: 8px; margin: 10px 0; }
  .salvaguardas .num-section { border-bottom: none; }
  .declaro-final { border: 1px solid #000; padding: 8px; margin: 10px 0; font-size: 10px; font-style: italic; background-color: #f9f9f9; line-height: 1.5; }
  .local-data { margin: 10px 0 15px 0; font-size: 10px; }
  .assinaturas { display: flex; justify-content: space-between; margin-top: 30px; }
  .assinatura-box { width: 47%; }
  .assinatura-linha { border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 9px; margin-top: 40px; }
  .assinatura-info { text-align: center; font-size: 9px; margin-top: 3px; }
  .footer { margin-top: 25px; padding-top: 8px; border-top: 1px solid #ccc; text-align: center; font-size: 8px; color: #666; }
`;

function abrirJanelaTcle(titulo: string, html: string): void {
  const w = window.open('', '_blank', 'width=850,height=1100,scrollbars=yes');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 600);
}

function htmlCabecalho(nome: string, referencia: string, brand?: TclePrintBrand): string {
  return `<div class="header-container">
  <div class="header-logo">${receiptLogoHtml(brand?.logo, brand?.cdEmpresa)}</div>
  <div class="header-titles">
    <div class="tcle-label">TERMO DE CONSENTIMENTO LIVRE E ESCLARECIDO</div>
    <div class="tcle-nome">${nome}</div>
    <div class="tcle-ref">Referência: ${referencia}</div>
  </div>
</div>`;
}

function htmlPaciente(f: any): string {
  const endereco = [f.dsEndereco, f.nrEndereco, f.bairro].filter(Boolean).join(', ');
  return `<div class="section-header">IDENTIFICAÇÃO DO PACIENTE</div>
<table class="info-table">
  <tr>
    <td class="label">Nome completo</td>
    <td colspan="5">${f.nmPaciente || ''}</td>
  </tr>
  <tr>
    <td class="label">Data de nascimento</td>
    <td>${f.dtNascimento || ''}</td>
    <td class="label">CPF</td>
    <td>${formatarCpf(f.cpf)}</td>
    <td class="label">RG</td>
    <td>${f.rg || ''}</td>
  </tr>
  <tr>
    <td class="label">Endereço</td>
    <td colspan="5">${endereco || ''}</td>
  </tr>
  <tr>
    <td class="label">Telefone</td>
    <td colspan="2">${f.telefone || ''}</td>
    <td class="label">Unidade de Atendimento</td>
    <td colspan="2">${f.estabelecimento || ''}</td>
  </tr>
</table>`;
}

function htmlSalvaguardas(): string {
  return `<div class="salvaguardas">
  <div class="num-section">DISPOSIÇÕES LEGAIS E SALVAGUARDAS</div>
  <ul class="bullet-list">
    <li>Este termo está em conformidade com o Código de Ética Médica (Resolução CFM nº 2.217/2018), Código Civil Brasileiro e Código de Defesa do Consumidor.</li>
    <li>A Clínica SIM e seus profissionais adotam todas as medidas técnicas e de segurança recomendadas pelas sociedades médicas especializadas e pelos órgãos reguladores.</li>
    <li>A assinatura deste termo não exime a responsabilidade civil e penal dos profissionais por eventuais erros, negligência, imperícia ou imprudência.</li>
    <li>O paciente tem o direito de revogar este consentimento a qualquer momento antes do procedimento, sem qualquer penalidade.</li>
    <li>Todas as informações prestadas são mantidas em sigilo médico, conforme Lei Geral de Proteção de Dados (LGPD – Lei 13.709/2018).</li>
    <li>Em caso de complicações, a Clínica SIM compromete-se a prestar todo atendimento necessário e/ou encaminhar para serviços especializados sem ônus adicional ao paciente.</li>
  </ul>
</div>`;
}

function htmlAssinaturas(f: any, userName: string, hoje: string, agora: string): string {
  return `<p class="local-data">Local: <strong>${f.estabelecimento || '_______________________________'}</strong> &nbsp;&nbsp;&nbsp; Data: <strong>${hoje}</strong></p>
<div class="assinaturas">
  <div class="assinatura-box">
    <div class="assinatura-linha">Assinatura do Paciente ou Responsável Legal</div>
    <div class="assinatura-info">${f.nmPaciente || ''}</div>
    ${f.nmResponsavel ? `<div class="assinatura-info" style="color:#555">Responsável: ${f.nmResponsavel}</div>` : ''}
  </div>
  <div class="assinatura-box">
    <div class="assinatura-linha">Assinatura do Médico / Profissional</div>
    <div class="assinatura-info">Nome: ${f.nmMedico || '________________________________'}</div>
    <div class="assinatura-info">CRM/Registro: ____________________________</div>
  </div>
</div>
<div class="footer">Impresso por: ${userName} &nbsp;|&nbsp; Data/Hora: ${agora} &nbsp;|&nbsp; Estabelecimento: ${f.estabelecimento || ''}</div>`;
}

function wrapPage(titulo: string, corpo: string): string {
  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>${titulo}</title><style>${CSS_TCLE}</style></head><body>${corpo}</body></html>`;
}

// ─── INSERÇÃO DE DIU ──────────────────────────────────────────────────────────

export function imprimirTcleDiuInsercao(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('INSERÇÃO DE DISPOSITIVO INTRAUTERINO (DIU)', 'Federação Brasileira das Associações de Ginecologia e Obstetrícia (FEBRASGO)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informada sobre o procedimento de <strong>INSERÇÃO DE DIU</strong>.</p>
<p class="tipo-row">Tipo de DIU: &nbsp;☐ DIU de Cobre &nbsp;&nbsp;&nbsp; ☐ DIU Hormonal (Mirena/Kyleena/outros): ___________________________</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">O DIU é um método contraceptivo de longa duração inserido dentro do útero. O DIU de cobre tem duração de 5–10 anos. O DIU hormonal libera progesterona e tem duração de 3–7 anos conforme o tipo.</p>
<div class="num-section">2. BENEFÍCIOS</div>
<p class="texto">Contracepção altamente eficaz (&gt;99%), longa duração, reversível, não interfere nas relações sexuais. O DIU hormonal pode reduzir cólicas menstruais e fluxo.</p>
<div class="num-section">3. RISCOS</div>
<ul class="bullet-list">
  <li><strong>Dor e cólicas:</strong> Durante e após a inserção (comum), geralmente melhora em horas a dias.</li>
  <li><strong>Perfuração uterina:</strong> Rara (1–2 por 1.000 inserções), pode requerer cirurgia.</li>
  <li><strong>Expulsão:</strong> 2–10% no primeiro ano, mais comum em nulíparas.</li>
  <li><strong>Infecção pélvica:</strong> Rara (1%), maior risco nas primeiras 3 semanas.</li>
  <li><strong>Sangramento irregular:</strong> Comum nos primeiros meses. DIU de cobre pode aumentar o fluxo menstrual.</li>
  <li><strong>Gravidez ectópica:</strong> Se engravidar com DIU, risco aumentado de gravidez fora do útero.</li>
  <li><strong>Desmaio ou reação vasovagal:</strong> Pode ocorrer durante a inserção.</li>
  <li><strong>Efeitos hormonais (DIU hormonal):</strong> Acne, alterações de humor, cistos ovarianos benignos, mastalgia.</li>
</ul>
<div class="num-section">4. CONTRAINDICAÇÕES</div>
<p class="texto">Gravidez ou suspeita, infecção genital ativa, câncer de colo ou endométrio, estenose de canal cervical, sangramento vaginal não investigado, malformações uterinas graves, doença inflamatória pélvica ativa.</p>
<div class="num-section">5. CUIDADOS E ORIENTAÇÕES</div>
<ul class="bullet-list">
  <li>Repouso relativo no dia da inserção.</li>
  <li>Evitar relações sexuais, uso de absorventes internos e duchas vaginais por 24–48 horas.</li>
  <li>Verificar os fios mensalmente após a menstruação.</li>
  <li>Retornar para ultrassom de controle em 4–6 semanas.</li>
  <li>Procurar atendimento se: dor intensa, febre, corrimento com odor fétido, sangramento abundante, não sentir os fios, atraso menstrual.</li>
  <li>O DIU <strong>não protege</strong> contra infecções sexualmente transmissíveis.</li>
</ul>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre possibilidade de gravidez, infecções prévias e alergias. Compreendi que o DIU não protege contra ISTs e os riscos do procedimento. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Inserção de DIU', wrapPage('TCLE - Inserção de DIU', corpo));
}

// ─── RETIRADA DE DIU ─────────────────────────────────────────────────────────

export function imprimirTcleDiuRetirada(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('RETIRADA DE DISPOSITIVO INTRAUTERINO (DIU)', 'Federação Brasileira das Associações de Ginecologia e Obstetrícia (FEBRASGO)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informada sobre o procedimento de <strong>RETIRADA DE DIU</strong>.</p>
<p class="tipo-row">Motivo: &nbsp;☐ Fim do prazo &nbsp;&nbsp; ☐ Desejo de engravidar &nbsp;&nbsp; ☐ Efeitos colaterais &nbsp;&nbsp; ☐ Outro: _______________</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Procedimento simples que consiste em remover o DIU através da tração cuidadosa dos fios que ficam no canal vaginal.</p>
<div class="num-section">2. BENEFÍCIOS</div>
<p class="texto">Remoção do método contraceptivo, retorno rápido da fertilidade, resolução de efeitos colaterais relacionados ao DIU.</p>
<div class="num-section">3. RISCOS</div>
<ul class="bullet-list">
  <li><strong>Dor ou cólica:</strong> Leve a moderada durante o procedimento.</li>
  <li><strong>Sangramento:</strong> Pequeno sangramento é normal.</li>
  <li><strong>Dificuldade na remoção:</strong> Se fios não visíveis ou DIU incorporado ao útero (raro), pode requerer procedimentos adicionais.</li>
  <li><strong>Quebra do DIU:</strong> Raramente os fios podem romper ou o DIU fragmentar.</li>
  <li><strong>Reação vasovagal:</strong> Tontura ou desmaio (raro).</li>
  <li><strong>Necessidade de procedimento adicional:</strong> Histeroscopia ou ultrassom se DIU não localizado ou fragmentado.</li>
</ul>
<div class="num-section">4. ORIENTAÇÕES APÓS RETIRADA</div>
<p class="texto">Pequeno sangramento é normal. A fertilidade retorna imediatamente (usar outro método contraceptivo se não desejar engravidar). O ciclo menstrual pode levar alguns meses para regular.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que compreendi que a fertilidade retorna imediatamente e que devo usar outro método se não desejar engravidar. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Retirada de DIU', wrapPage('TCLE - Retirada de DIU', corpo));
}

// ─── CORE BIOPSY DE MAMA ─────────────────────────────────────────────────────

export function imprimirTcleCoreBiopsy(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('CORE BIOPSY DE MAMA (Biópsia de Fragmento por Agulha Grossa)', 'Sociedade Brasileira de Mastologia (SBM) / Colégio Brasileiro de Radiologia (CBR)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informada sobre o procedimento de <strong>CORE BIOPSY DE MAMA</strong>.</p>
<p class="tipo-row">Lesão: &nbsp;☐ Mama direita &nbsp;&nbsp; ☐ Mama esquerda &nbsp;&nbsp; Localização: _________________ &nbsp;&nbsp; Método: &nbsp;☐ Ultrassom &nbsp;☐ Estereotaxia &nbsp;☐ Ressonância</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Procedimento minimamente invasivo que utiliza agulha grossa (14G) acoplada a dispositivo automático para remover fragmentos cilíndricos de tecido mamário de lesões suspeitas.</p>
<div class="num-section">2. MARCADOR (CLIP)</div>
<p class="texto">Um pequeno marcador metálico (clip de titânio) pode ser deixado no local da biópsia para localizar a lesão em exames futuros. É compatível com ressonância e não dispara alarmes.</p>
<div class="num-section">3. BENEFÍCIOS</div>
<p class="texto">Diagnóstico histopatológico preciso, planejamento terapêutico antes de cirurgia definitiva, evita cirurgias diagnósticas, procedimento ambulatorial, acurácia &gt;95%.</p>
<div class="num-section">4. RISCOS</div>
<p class="texto">Dor ou desconforto leve a moderado, hematoma (10–30%), sangramento, infecção (&lt;1%), equimose, cicatriz pequena (2–3mm), pneumotórax (muito raro), desmaio, resultado discordante, subamostragem (5–10% em lesões de alto risco).</p>
<div class="num-section">5. LESÕES DE ALTO RISCO</div>
<p class="texto">Alguns resultados (hiperplasia ductal atípica, neoplasia lobular in situ, lesão papilífera, cicatriz radial) geralmente requerem cirurgia para excisão completa, pois podem coexistir com câncer em 10–30%.</p>
<div class="num-section">6. CUIDADOS PÓS-PROCEDIMENTO</div>
<ul class="bullet-list">
  <li>Manter curativo compressivo por 24h, usar sutiã firme por 3–5 dias.</li>
  <li>Aplicar gelo (15–20 min, 3–4x/dia) nas primeiras 48h.</li>
  <li>Evitar esforço físico por 48–72h, não molhar por 24h.</li>
  <li>Procurar atendimento se: hematoma grande progressivo, sangramento abundante, febre.</li>
</ul>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações anticoagulantes, alergias, cirurgias mamárias prévias e implantes. Compreendi que resultado benigno pode requerer cirurgia se lesão de alto risco ou discordância com imagem. Consinto com inserção de marcador. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Core Biopsy de Mama', wrapPage('TCLE - Core Biopsy de Mama', corpo));
}

// ─── INFILTRAÇÃO ARTICULAR ────────────────────────────────────────────────────

export function imprimirTcleInfiltracao(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('INFILTRAÇÃO ARTICULAR', 'Sociedade Brasileira de Ortopedia e Traumatologia (SBOT) / Sociedade Brasileira de Reumatologia (SBR)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>INFILTRAÇÃO ARTICULAR</strong>.</p>
<p class="tipo-row">Articulação: _______________ &nbsp;&nbsp; Medicação: &nbsp;☐ Corticosteroide &nbsp;☐ Ácido Hialurônico &nbsp;☐ Outro: _______________</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Aplicação de medicamentos diretamente dentro da articulação através de uma agulha, visando tratar processos inflamatórios, degenerativos ou dolorosos.</p>
<div class="num-section">2. BENEFÍCIOS</div>
<p class="texto">Alívio da dor articular, redução da inflamação local, melhora da mobilidade articular, adiamento de cirurgia em alguns casos.</p>
<div class="num-section">3. RISCOS</div>
<p class="texto">Dor pós-infiltração (flare), infecção articular (&lt;0,1% mas grave), reação alérgica, sangramento/hematoma, lesão de cartilagem com infiltrações repetidas, atrofia de pele, ruptura tendínea (rara), efeitos sistêmicos do corticoide.</p>
<div class="num-section">4. CONTRAINDICAÇÕES</div>
<p class="texto">Infecção ativa, fratura articular não consolidada, artropatia neuropática, distúrbio grave de coagulação, alergia aos componentes.</p>
<div class="num-section">5. LIMITAÇÕES</div>
<p class="texto">O efeito varia individualmente. Não é cura definitiva. Pode ser necessária mais de uma aplicação. Infiltrações de corticoide devem ser limitadas (máximo 3–4 por ano na mesma articulação).</p>
<div class="num-section">6. CUIDADOS PÓS-PROCEDIMENTO</div>
<p class="texto">Repouso relativo por 24–48h, aplicar gelo se dor, evitar esforços intensos por 48–72h. Procurar atendimento urgente se: dor intensa progressiva, calor, vermelhidão, febre.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações anticoagulantes, alergias, diabetes e infecções recentes. Compreendi que não é curativo e os riscos, especialmente de infecção. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Infiltração Articular', wrapPage('TCLE - Infiltração Articular', corpo));
}

// ─── PAAF DE TIREOIDE ─────────────────────────────────────────────────────────

export function imprimirTclePaafTireoide(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('PAAF DE TIREOIDE (Punção Aspirativa por Agulha Fina)', 'Sociedade Brasileira de Endocrinologia e Metabologia (SBEM) / Sociedade Brasileira de Patologia (SBP)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>PAAF DE TIREOIDE</strong>.</p>
<p class="tipo-row">Nódulo(s): ___________________________________________________________________</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Procedimento minimamente invasivo que utiliza agulha fina para aspirar células de nódulos tireoidianos. O material é analisado microscopicamente (exame citopatológico).</p>
<div class="num-section">2. SISTEMA BETHESDA</div>
<p class="texto">Resultado classificado em 6 categorias: I-Não diagnóstico; II-Benigno; III-Atipia; IV-Neoplasia folicular; V-Suspeito de malignidade; VI-Maligno.</p>
<div class="num-section">3. BENEFÍCIOS</div>
<p class="texto">Diferenciação entre nódulos benignos e suspeitos, orientação de conduta terapêutica, evita cirurgias desnecessárias, procedimento ambulatorial, baixo risco.</p>
<div class="num-section">4. RISCOS</div>
<p class="texto">Dor ou desconforto leve, hematoma cervical (5–10%), sangramento pequeno, infecção (&lt;0,1%), lesão de estruturas adjacentes (muito rara), desmaio, material insuficiente (5–15%), resultado falso-negativo (1–5%).</p>
<div class="num-section">5. LIMITAÇÕES</div>
<p class="texto">Alguns tipos de câncer não podem ser definitivamente diagnosticados apenas por PAAF. Resultados indeterminados podem necessitar testes moleculares ou cirurgia.</p>
<div class="num-section">6. CUIDADOS PÓS-PROCEDIMENTO</div>
<ul class="bullet-list">
  <li>Aplicar compressão por 5–10 minutos, aplicar gelo se inchaço.</li>
  <li>Evitar esforço físico nas primeiras 24h.</li>
  <li>Procurar atendimento se: hematoma grande, dificuldade respiratória, febre.</li>
</ul>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações anticoagulantes, alergias e distúrbios de coagulação. Compreendi que pode não fornecer diagnóstico definitivo e que um resultado benigno não elimina completamente a possibilidade de malignidade. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - PAAF de Tireoide', wrapPage('TCLE - PAAF de Tireoide', corpo));
}

// ─── BIÓPSIA DE PELE - PUNCH ──────────────────────────────────────────────────

export function imprimirTclePunchPele(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('BIÓPSIA DE PELE - PUNCH', 'Sociedade Brasileira de Dermatologia (SBD) / Sociedade Brasileira de Cirurgia Dermatológica (SBCD)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>BIÓPSIA DE PELE POR PUNCH</strong>.</p>
<p class="tipo-row">Local da biópsia: ____________________________________________________________________________</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Procedimento cirúrgico de pequeno porte que utiliza um instrumento cilíndrico cortante (punch) para remover um pequeno fragmento circular de pele (3 a 6 mm) para análise histopatológica.</p>
<div class="num-section">2. BENEFÍCIOS</div>
<p class="texto">Diagnóstico preciso de lesões cutâneas, diferenciação entre lesões benignas e malignas, identificação de doenças inflamatórias da pele.</p>
<div class="num-section">3. RISCOS</div>
<p class="texto">Sangramento, infecção (&lt;1%), cicatriz permanente, alteração de pigmentação, hematoma, reações à anestesia local.</p>
<div class="num-section">4. CUIDADOS PÓS-PROCEDIMENTO</div>
<ul class="bullet-list">
  <li>Manter curativo limpo e seco por 24–48h, não molhar nas primeiras 24h.</li>
  <li>Evitar esforço físico intenso por 48h.</li>
  <li>Retornar para retirada de pontos (7–14 dias).</li>
</ul>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações anticoagulantes, alergias e tendência a queloides. Compreendi que haverá cicatriz permanente. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Biópsia de Pele - Punch', wrapPage('TCLE - Biópsia de Pele - Punch', corpo));
}

// ─── COLONOSCOPIA ─────────────────────────────────────────────────────────────

export function imprimirTcleColonoscopia(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('COLONOSCOPIA', 'Sociedade Brasileira de Endoscopia Digestiva (SOBED)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>COLONOSCOPIA</strong>.</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Exame que permite visualizar o interior do intestino grosso (cólon) e íleo terminal através de um tubo flexível com câmera introduzido pelo ânus.</p>
<div class="num-section">2. PREPARO INTESTINAL</div>
<p class="texto">É essencial realizar preparo intestinal rigoroso conforme orientações. Preparo inadequado prejudica a qualidade do exame e pode requerer repetição.</p>
<div class="num-section">3. SEDAÇÃO E ACOMPANHANTE</div>
<p class="texto">Será administrada sedação endovenosa. É <strong>OBRIGATÓRIO</strong> estar acompanhado de adulto responsável. <strong>NÃO</strong> dirigir ou operar máquinas por 12 horas.</p>
<div class="num-section">4. RISCOS E COMPLICAÇÕES</div>
<p class="texto">Complicações são raras, mas incluem:</p>
<ul class="bullet-list">
  <li><strong>Perfuração intestinal</strong> (0,1–0,2% em diagnósticos; 0,5–2% em polipectomias): pode requerer cirurgia de urgência.</li>
  <li><strong>Sangramento</strong> (0,1–0,6% em diagnósticos; 0,5–3% em polipectomias): geralmente controlado durante o exame.</li>
  <li><strong>Reações à sedação:</strong> depressão respiratória, arritmias, hipotensão, reações alérgicas.</li>
  <li><strong>Síndrome pós-polipectomia:</strong> dor abdominal sem perfuração (1–3% após polipectomias).</li>
  <li>Distensão abdominal e cólicas pelo ar insuflado.</li>
  <li>Eventos cardiovasculares em pacientes de alto risco.</li>
</ul>
<div class="num-section">5. BENEFÍCIOS</div>
<p class="texto">Diagnóstico de pólipos, tumores, doença inflamatória intestinal, divertículos, sangramentos. Prevenção de câncer colorretal através da remoção de pólipos.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações, alergias, cirurgias prévias. Compreendi a importância do preparo adequado e os riscos do procedimento. Consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Colonoscopia', wrapPage('TCLE - Colonoscopia', corpo));
}

// ─── ECOCARDIOGRAMA ───────────────────────────────────────────────────────────

export function imprimirTcleEcocardiograma(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('ECOCARDIOGRAMA', 'Sociedade Brasileira de Cardiologia (SBC) / Departamento de Imagem Cardiovascular (DIC)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>ECOCARDIOGRAMA</strong> que será realizado, tendo recebido as seguintes informações:</p>
<div class="num-section">1. NATUREZA DO EXAME</div>
<p class="texto">O ecocardiograma é um exame de ultrassom que avalia a estrutura e o funcionamento do coração, permitindo visualizar válvulas, câmaras cardíacas, espessura das paredes e fluxo sanguíneo. O exame é não invasivo e utiliza ondas sonoras de alta frequência.</p>
<div class="num-section">2. COMO SERÁ REALIZADO</div>
<p class="texto">Será aplicado um gel sobre o tórax e um transdutor será posicionado em diferentes áreas para captar imagens do coração. O exame dura aproximadamente 20 a 40 minutos. É necessário ficar em repouso e, eventualmente, deitar de lado ou modificar a respiração conforme orientação do examinador.</p>
<div class="num-section">3. BENEFÍCIOS ESPERADOS</div>
<p class="texto">Diagnóstico preciso de doenças cardíacas, avaliação da função do coração, detecção de alterações valvares, avaliação de sopros cardíacos e monitoramento de doenças cardíacas conhecidas.</p>
<div class="num-section">4. RISCOS E DESCONFORTOS</div>
<p class="texto">O ecocardiograma é um exame extremamente seguro. Pode haver leve desconforto pela pressão do transdutor ou pela posição solicitada. Não há exposição à radiação. Pacientes com dificuldade respiratória podem sentir desconforto ao deitar.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que compreendi todas as informações prestadas, tive oportunidade de fazer perguntas, que foram respondidas de forma clara, e consinto voluntariamente com a realização do exame.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Ecocardiograma', wrapPage('TCLE - Ecocardiograma', corpo));
}

// ─── ENDOSCOPIA DIGESTIVA ALTA ────────────────────────────────────────────────

export function imprimirTcleEndoscopia(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('ENDOSCOPIA DIGESTIVA ALTA', 'Sociedade Brasileira de Endoscopia Digestiva (SOBED)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>ENDOSCOPIA DIGESTIVA ALTA</strong>.</p>
<div class="num-section">1. NATUREZA DO PROCEDIMENTO</div>
<p class="texto">Exame que permite visualizar o esôfago, estômago e duodeno através de um tubo flexível com câmera introduzido pela boca.</p>
<div class="num-section">2. SEDAÇÃO E ACOMPANHANTE</div>
<p class="texto">Será administrada sedação endovenosa. É <strong>OBRIGATÓRIO</strong> estar acompanhado de adulto responsável. <strong>NÃO</strong> dirigir, operar máquinas, ingerir álcool ou assinar documentos por 12 horas.</p>
<div class="num-section">3. BENEFÍCIOS</div>
<p class="texto">Diagnóstico de gastrite, úlceras, refluxo, tumores, pólipos, sangramentos. Possibilidade de biópsias e tratamentos durante o exame.</p>
<div class="num-section">4. RISCOS E COMPLICAÇÕES</div>
<p class="texto">Complicações são raras (menos de 1% dos casos), mas incluem:</p>
<ul class="bullet-list">
  <li><strong>Perfuração</strong> do esôfago, estômago ou duodeno (0,03–0,1%): pode requerer cirurgia de urgência.</li>
  <li><strong>Sangramento</strong> (0,1–0,5%): especialmente após biópsias ou remoção de pólipos.</li>
  <li><strong>Reações à sedação:</strong> depressão respiratória, arritmias cardíacas, reações alérgicas.</li>
  <li><strong>Aspiração</strong> de conteúdo gástrico para pulmões (raro, se jejum adequado).</li>
  <li>Infecção (rara).</li>
  <li>Lesão dentária (rara, pelo protetor bucal).</li>
</ul>
<div class="num-section">5. CUIDADOS PÓS-EXAME</div>
<p class="texto">Alimentação leve após recuperação. Procurar atendimento se: dor abdominal intensa, vômitos com sangue, fezes escuras, febre ou dificuldade respiratória.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre medicações, alergias e doenças prévias. Compreendi os riscos e consinto voluntariamente com a realização do exame.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Endoscopia Digestiva Alta', wrapPage('TCLE - Endoscopia Digestiva Alta', corpo));
}

// ─── RADIOGRAFIA ──────────────────────────────────────────────────────────────

export function imprimirTcleRadiografia(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('RADIOGRAFIA (RAIO-X)', 'Colégio Brasileiro de Radiologia (CBR)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>RADIOGRAFIA</strong>.</p>
<p class="tipo-row">Região a ser examinada: __________________________________________________________________________</p>
<div class="num-section">1. NATUREZA DO EXAME</div>
<p class="texto">A radiografia utiliza raios-X para criar imagens de estruturas internas do corpo, principalmente ossos, mas também pulmões, coração e outros órgãos.</p>
<div class="num-section">2. RISCOS</div>
<p class="texto">Exposição à radiação ionizante: A dose é mínima e controlada. O benefício diagnóstico supera o risco. Gestantes devem sempre informar sobre gravidez.</p>
<div class="num-section">3. BENEFÍCIOS</div>
<p class="texto">Detecção de fraturas ósseas, luxações, avaliação de doenças pulmonares, avaliação do coração e grandes vasos, detecção de corpos estranhos.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre possibilidade de gravidez. Compreendi as informações e consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Radiografia', wrapPage('TCLE - Radiografia', corpo));
}

// ─── TESTE ERGOMÉTRICO ────────────────────────────────────────────────────────

export function imprimirTcleTesteErgometrico(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('TESTE ERGOMÉTRICO', 'Sociedade Brasileira de Cardiologia (SBC) / Departamento de Ergometria e Reabilitação Cardiovascular (DERC)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>TESTE ERGOMÉTRICO</strong> que será realizado, tendo recebido as seguintes informações:</p>
<div class="num-section">1. NATUREZA DO EXAME</div>
<p class="texto">O teste ergométrico (ou teste de esforço) avalia a resposta do coração durante atividade física progressiva, geralmente em esteira ou bicicleta ergométrica, monitorando eletrocardiograma, pressão arterial e sintomas.</p>
<div class="num-section">2. COMO SERÁ REALIZADO</div>
<p class="texto">Eletrodos serão fixados no tórax para monitoramento cardíaco contínuo. O paciente caminhará ou pedalará com intensidade progressivamente maior até atingir a frequência cardíaca alvo, apresentar sintomas limitantes ou alterações que justifiquem interrupção. O exame dura de 15 a 30 minutos. É necessário comparecer com roupas leves e calçado adequado para exercício.</p>
<div class="num-section">3. BENEFÍCIOS ESPERADOS</div>
<p class="texto">Avaliação da capacidade funcional cardiovascular, detecção de isquemia miocárdica, avaliação de arritmias induzidas por esforço, determinação de prognóstico em cardiopatas e orientação para atividades físicas.</p>
<div class="num-section">4. RISCOS E COMPLICAÇÕES</div>
<p class="texto">Fadiga, falta de ar, palpitações, tontura e desconforto torácico são esperados durante o esforço máximo. Complicações graves são raras (1 a 5 por 10.000 exames) e incluem arritmias graves, infarto agudo do miocárdio ou morte súbita. A equipe está preparada para atendimento emergencial.</p>
<div class="num-section">5. CONTRAINDICAÇÕES</div>
<p class="texto">Infarto agudo recente, angina instável, arritmias graves não controladas, insuficiência cardíaca descompensada, estenose aórtica grave, miocardite ou pericardite aguda, embolia pulmonar recente, dissecção de aorta.</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei todas as condições de saúde relevantes, compreendi os riscos do procedimento, tive oportunidade de fazer perguntas, que foram respondidas de forma clara, e consinto voluntariamente com a realização do exame.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Teste Ergométrico', wrapPage('TCLE - Teste Ergométrico', corpo));
}

// ─── TOMOGRAFIA COMPUTADORIZADA ───────────────────────────────────────────────

export function imprimirTcleTomografia(f: any, userName: string, brand?: TclePrintBrand): void {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const agora = new Date().toLocaleString('pt-BR');

  const corpo = `
${htmlCabecalho('TOMOGRAFIA COMPUTADORIZADA', 'Colégio Brasileiro de Radiologia (CBR)', brand)}
${htmlPaciente(f)}
<p class="declaro">Declaro que fui devidamente informado(a) sobre o procedimento de <strong>TOMOGRAFIA COMPUTADORIZADA</strong>.</p>
<p class="tipo-row">Região a ser examinada: _______________________________________________</p>
<p class="tipo-row">☐ Exame <strong>COM</strong> contraste intravenoso &nbsp;&nbsp;&nbsp; ☐ Exame <strong>SEM</strong> contraste</p>
<div class="num-section">1. NATUREZA DO EXAME</div>
<p class="texto">A tomografia computadorizada utiliza raios-X e processamento computadorizado para criar imagens detalhadas em cortes transversais do corpo.</p>
<div class="num-section">2. RISCOS PRINCIPAIS</div>
<ul class="bullet-list">
  <li><strong>Exposição à radiação ionizante:</strong> A dose é controlada e justificada pelo benefício diagnóstico. Gestantes devem informar a possibilidade de gravidez.</li>
  <li><strong>Reações ao contraste iodado (se utilizado):</strong> Leves (náusea, urticária, coceira) ocorrem em 3–15% dos casos. Moderadas (vômitos, broncoespasmo) em 0,5–2%. Graves (choque anafilático, parada cardíaca) são raras (&lt;0,1%). Nefrotoxicidade pode ocorrer em pacientes com função renal comprometida.</li>
  <li><strong>Extravasamento do contraste:</strong> Pode causar dor, inchaço e, raramente, lesão tecidual no local da injeção.</li>
</ul>
<div class="num-section">3. CONTRAINDICAÇÕES</div>
<p class="texto">Gravidez (especialmente primeiro trimestre), alergia grave prévia a contraste iodado, insuficiência renal grave não dialítica (para exames com contraste).</p>
${htmlSalvaguardas()}
<div class="declaro-final">Declaro que informei sobre alergias, medicações, problemas renais e possibilidade de gravidez. Compreendi os riscos e consinto voluntariamente.</div>
${htmlAssinaturas(f, userName, hoje, agora)}`;

  abrirJanelaTcle('TCLE - Tomografia Computadorizada', wrapPage('TCLE - Tomografia Computadorizada', corpo));
}

// ─── DISPATCHER PRINCIPAL ─────────────────────────────────────────────────────

export type TcleKey = 'diu-insercao' | 'diu-retirada' | 'core-biopsy-mama' | 'infiltracao-articular'
  | 'paaf-tireoide' | 'punch-pele' | 'colonoscopia' | 'ecocardiograma'
  | 'endoscopia-digestiva' | 'radiografia' | 'teste-ergometrico' | 'tomografia';

export function imprimirTcle(key: TcleKey, f: any, userName: string, brand?: TclePrintBrand): boolean {
  switch (key) {
    case 'diu-insercao':         imprimirTcleDiuInsercao(f, userName, brand);    return true;
    case 'diu-retirada':         imprimirTcleDiuRetirada(f, userName, brand);    return true;
    case 'core-biopsy-mama':     imprimirTcleCoreBiopsy(f, userName, brand);     return true;
    case 'infiltracao-articular':imprimirTcleInfiltracao(f, userName, brand);    return true;
    case 'paaf-tireoide':        imprimirTclePaafTireoide(f, userName, brand);   return true;
    case 'punch-pele':           imprimirTclePunchPele(f, userName, brand);      return true;
    case 'colonoscopia':         imprimirTcleColonoscopia(f, userName, brand);   return true;
    case 'ecocardiograma':       imprimirTcleEcocardiograma(f, userName, brand); return true;
    case 'endoscopia-digestiva': imprimirTcleEndoscopia(f, userName, brand);     return true;
    case 'radiografia':          imprimirTcleRadiografia(f, userName, brand);    return true;
    case 'teste-ergometrico':    imprimirTcleTesteErgometrico(f, userName, brand); return true;
    case 'tomografia':           imprimirTcleTomografia(f, userName, brand);     return true;
    default: return false;
  }
}
